import React, { useState, useEffect, useCallback } from 'react';
import { useStyles } from './SessionsParametersCardLimit.styles';
import {
  CardLimits,
  UpdateCardLimitsResponse,
} from '../SessionsParametersDialogContent/SessionsParametersDialogContent.types';
import { CardLimitsData } from './SessionsParametersCardLimit.types';
import { SessionsParametersLimitCell } from './SessionsParametersLimitCell/SessionsParametersLimitCell.component';
import { calculateCardLimits, saveCardLimits } from '../../../../services/exams/sessions/sessions.service';
import { getChangesFromArrayOfObject } from '../../../../utils/getChangesFromArrayOfObject';
import { InputTable } from '../../../../components/InputTable/InputTable.component';
import { ErrorList, InputTableColumns } from '../../../../components/InputTable/InputTable.type';
import { useLayoutContext } from '../../../../Context/Layout.context';
import { AxiosResponse } from 'axios';
import { OverflowTypograpy } from '../../../../components/OverflowTypography/OverflowTypography.component';
import { AssessmentSessionState } from '../../../Dashboard/Dashboard.types';
import { getValidationMessage } from './helpers';
import { useTranslation } from 'react-i18next';
import { ButtonOne } from '../../../../components/ButtonOne/ButtonOne.component';
import { Prompt } from '../../../../components/Prompt/Prompt.component';

interface SessionsParametersCardLimitProps {
  apiResponse: string;
  items: CardLimits[];
  onClose: () => void;
  reload: () => void;
  isExaminerView: boolean;
  isLoading: boolean;
  onChange?: (isDataChanged: boolean) => void;
  assessmentSessionStateId: AssessmentSessionState;
  sheetId: string;
}

const getSessionsParametersKeyDefaultValue = (key: string): number => {
  switch (key) {
    case 'limitDaily':
    case 'limitDesk':
    case 'limitTotal':
      return 1;
    case 'extendedAccessDuration':
      return 24;
    default:
      return 1;
  }
};

export const SessionsParametersCardLimit: React.FC<SessionsParametersCardLimitProps> = ({
  items,
  apiResponse,
  reload,
  onClose,
  isLoading,
  isExaminerView,
  onChange,
  assessmentSessionStateId,
  sheetId,
}) => {
  const [parameters, setParameters] = useState<CardLimits[]>(items);
  const [invalidType, setInvalidType] = useState<ErrorList[]>([]);
  const [edited, setEdited] = useState<boolean>(false);
  const [calculateCardLimitResultError, setCalculateCardLimitResultError] = useState<boolean>(false);
  const [groupsWithError, setGroupsWithError] = useState<string[]>([]);

  const styles = useStyles();
  const { openAlert, showLoader } = useLayoutContext();
  const { t } = useTranslation();

  const columns: InputTableColumns[] = [
    {
      id: 'examTaskGroupName',
      label: t('sessionsParameters:sessionsParametersCardLimit.table.examTaskGroupName'),
    },
    {
      id: 'limitTotal',
      label: t('sessionsParameters:sessionsParametersCardLimit.table.limitTotal'),
      tooltipText: 'Docelowa liczba rozwiązań grup zadań',
    },
    {
      id: 'limitDaily',
      label: t('sessionsParameters:sessionsParametersCardLimit.table.limitDaily'),
      tooltipText: 'Dobowa liczba rozwiązań grup zadań',
    },
    {
      id: 'limitDesk',
      label: t('sessionsParameters:sessionsParametersCardLimit.table.limitDesk'),
      tooltipText: 'Jednoczesna dopuszczalna liczba rozwiązań grup zadań',
    },
    {
      id: 'extendedAccessDuration',
      label: t('sessionsParameters:sessionsParametersCardLimit.table.extendedAccessDuration'),
      tooltipText: 'Zdefiniowanie okresu przedłużonego dostępu',
    },
  ];

  useEffect(() => {
    setParameters(items);
  }, [items]);

  const validateLimitTotal = (value: number, limits: CardLimits): boolean => {
    if (value < 0) {
      return false;
    }

    if (!limits.limitDaily && !limits.limitDesk) {
      return true;
    }

    if (limits.limitDaily && value && value < limits.limitDaily) {
      return false;
    }

    if (limits.limitDesk && value && value < limits.limitDesk) {
      return false;
    }

    return true;
  };

  const validateLimitDaily = (value: number, limits: CardLimits): boolean => {
    if (value < 0) {
      return false;
    }

    if (!limits.limitTotal && !limits.limitDesk) {
      return true;
    }

    if (limits.limitDesk && value && value < limits.limitDesk) {
      return false;
    }

    if (limits.limitTotal && value && value > limits.limitTotal) {
      return false;
    }

    if (limits.limitDesk && limits.limitDesk > limits.limitTotal) {
      return false;
    }

    return true;
  };

  const validateLimitDesk = (value: number, limits: CardLimits): boolean => {
    if (value < 0) {
      return false;
    }

    if (!limits.limitTotal && !limits.limitDaily) {
      return true;
    }

    if (limits.limitTotal && limits.limitDaily > limits.limitTotal) {
      return false;
    }

    if (limits.limitTotal && value && value > limits.limitTotal) {
      return false;
    }

    if (limits.limitDaily && value && value > limits.limitDaily) {
      return false;
    }
    return true;
  };

  const validateExtendedAccessDuration = (value: number | null): boolean => {
    if (value === null) {
      return true;
    }
    if (value < 0) {
      return false;
    }
    return true;
  };

  const valueValidation = (type: keyof CardLimits, value: number | null, limits: CardLimits): boolean => {
    if (type === 'limitTotal') {
      const isValid = value !== null && validateLimitTotal(value, limits);
      if (isValid) {
        setInvalidType((prev) => prev.filter((e) => e.id !== limits.examTaskGroupId));
      } else {
        setInvalidType((prev) => [...prev, { type, id: limits.examTaskGroupId }]);
      }
      return isValid;
    } else if (type === 'limitDaily') {
      const isValid = value !== null && validateLimitDaily(value, limits);
      if (isValid) {
        setInvalidType((prev) => prev.filter((e) => e.id !== limits.examTaskGroupId));
      } else {
        setInvalidType((prev) => [...prev, { type, id: limits.examTaskGroupId }]);
      }
      return isValid;
    } else if (type === 'limitDesk') {
      const isValid = value !== null && validateLimitDesk(value, limits);
      if (isValid) {
        setInvalidType((prev) => prev.filter((e) => e.id !== limits.examTaskGroupId));
      } else {
        setInvalidType((prev) => [...prev, { type, id: limits.examTaskGroupId }]);
      }
      return isValid;
    } else if (type === 'extendedAccessDuration') {
      const isValid = validateExtendedAccessDuration(value);
      if (isValid) {
        setInvalidType((prev) => prev.filter((e) => e.id !== limits.examTaskGroupId));
      } else {
        setInvalidType((prev) => [...prev, { type, id: limits.examTaskGroupId }]);
      }
      return isValid;
    } else {
      setInvalidType((prev) => [...prev]);
      return true;
    }
  };

  const handleValueCallback = useCallback(
    (key: keyof CardLimits, value: number | null, id: string | number) => {
      setParameters((state: CardLimits[]) => {
        return state.map((s: CardLimits) => {
          if (s.examTaskGroupId === id) {
            s[key] = value as never;
          }

          return s;
        });
      });
    },
    [parameters, setParameters],
  );

  const handleSwitchCallback = useCallback(
    (key: keyof CardLimits, value: boolean, id: string | number) => {
      setParameters((state) => {
        return state.map((s) => {
          if (s.examTaskGroupId === id) {
            s[key] = (value ? getSessionsParametersKeyDefaultValue(key) : null) as never;
          }

          return s;
        });
      });
    },
    [parameters, setParameters],
  );

  useEffect(() => {
    setEdited(JSON.stringify(parameters) !== apiResponse);
  }, [parameters, apiResponse]);

  const handleDiscardCallback = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleSaveCallback = useCallback(() => {
    const apiData: CardLimits[] = JSON.parse(apiResponse);
    const filterData: CardLimits[] = getChangesFromArrayOfObject<CardLimits>(apiData, parameters);
    const data: CardLimitsData = {
      cardLimits: filterData.map((p) => {
        return {
          examTaskGroupId: p.examTaskGroupId,
          examinerPermissionId: '',
          limitDesk: p.limitDesk ?? null,
          limitDaily: p.limitDaily ?? null,
          limitTotal: p.limitTotal ?? null,
          extendedAccessDuration:
            p.extendedAccessDuration !== null || p.extendedAccessDuration === 0 ? p.extendedAccessDuration : null,
        };
      }),
    };
    saveCardLimits(data, isExaminerView)
      .then((res: AxiosResponse<UpdateCardLimitsResponse>) => {
        if (res.data === UpdateCardLimitsResponse.Success) {
          openAlert('success');
          setEdited(false);
        } else {
          openAlert('error');
          reload();
        }
      })
      .catch(() => {
        openAlert('error');
        reload();
      });
  }, [parameters, onClose]);

  const getCalcLimit = (limit: number, parameter: CardLimits, result?: number) => {
    if (result === 0) {
      return limit === 0 ? (null as never) : limit;
    } else {
      return parameter.limitDaily === 0 ? (null as never) : parameter.limitDaily;
    }
  };
  const handleCalculateCardLimits = () => {
    showLoader(true);
    setGroupsWithError([]);
    calculateCardLimits(sheetId)
      .then((response) => {
        const data: CardLimits[] = [];
        response.data.forEach((p) => {
          const parameter = parameters.find((e) => e.examTaskGroupId === p.examTaskGroupId);
          if (parameter) {
            data.push({
              id: p.examTaskGroupId,
              examTaskGroupName: p.examTaskGroupName,
              examTaskGroupId: p.examTaskGroupId,
              examinerPermissionId: '',
              limitDesk: getCalcLimit(p.limitDesk, p, p.calculateCardLimitResult),
              limitDaily: getCalcLimit(p.limitDaily, p, p.calculateCardLimitResult),
              limitTotal: getCalcLimit(p.limitTotal, p, p.calculateCardLimitResult),
              extendedAccessDuration: parameter.extendedAccessDuration,
              validSince: parameter.validSince,
              calculateCardLimitResult: p.calculateCardLimitResult,
            });
          }
          if (p.calculateCardLimitResult === 1) {
            setGroupsWithError((prevState) => [...prevState, p.examTaskGroupName]);
            setCalculateCardLimitResultError(true);
          }
        });
        setParameters(data);
      })
      .catch(() => {
        setParameters([]);
      })
      .finally(() => {
        showLoader(false);
      });
  };

  const getAriaLabel = (type: string, examTaskGroupName: string) => {
    const limitLabel = `liczba RGZ dla grupy zadań ${examTaskGroupName}`;
    switch (type) {
      case 'limitDaily':
        return `Dobowa ${limitLabel}`;
      case 'limitDesk':
        return `Docelowa ${limitLabel}`;
      case 'limitTotal':
        return `Całkowita ${limitLabel}`;
      case 'extendedAccessDuration':
        return `Okres przedłużonego dostępu dla grupy zadań ${examTaskGroupName}`;
    }
  };

  const getRowComponent = (type: string, value: CardLimits, isValid: boolean): React.ReactNode | string | null => {
    switch (type) {
      case 'examTaskGroupName':
        return (
          <OverflowTypograpy
            style={{
              display: 'inline-block',
              fontWeight: 'bolder',
              fontSize: '1rem',
              color: '#3C419A',
              width: '160px',
            }}
          >
            {value[type]}
          </OverflowTypograpy>
        );
      case 'limitDaily':
      case 'limitDesk':
      case 'limitTotal':
      case 'extendedAccessDuration':
        return (
          <SessionsParametersLimitCell<CardLimits>
            type={type}
            value={value}
            initialValue={value[type]}
            handleValueChange={handleValueCallback}
            handleSwitchChange={handleSwitchCallback}
            validation={valueValidation}
            isValid={isValid}
            idKey={'examTaskGroupId'}
            tripleSwitchMode={type === 'extendedAccessDuration'}
            isSwitchDisabled={assessmentSessionStateId !== AssessmentSessionState.New}
            ariaLabel={getAriaLabel(type, value.examTaskGroupName)}
            examTaskGroupName={value.examTaskGroupName}
            isCardLimit={true}
          />
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    onChange && onChange(edited);
  }, [edited]);

  const emptyMessage = 'Brak wyników';

  return (
    <>
      <div className={styles.buttonContainer}>
        <ButtonOne variant="contained" color="primary" onClick={() => handleCalculateCardLimits()}>
          Przelicz automatycznie
        </ButtonOne>
      </div>
      <InputTable<CardLimits>
        isLoading={isLoading}
        columns={columns}
        parameters={parameters}
        items={items}
        keyParam={'examTaskGroupId'}
        invalidType={invalidType}
        headerText={t('sessionsParameters:sessionsParametersCardLimit.header')}
        onCancel={handleDiscardCallback}
        onSave={handleSaveCallback}
        getValidationMessage={getValidationMessage}
        getRowComponent={getRowComponent}
        edited={edited}
        emptyMessage={emptyMessage}
        containerHeight={'390px'}
      />
      {calculateCardLimitResultError && (
        <Prompt
          type="info"
          size="sm"
          open={calculateCardLimitResultError}
          onConfirm={() => setCalculateCardLimitResultError(false)}
          mainActionButtonText="POWRÓT DO LIMITÓW"
          mainText={
            <>
              {`Nie możesz automatycznie przeliczyć celów dla ${groupsWithError.join(', ')}, ponieważ dla ${
                groupsWithError.length === 1 ? 'tej grupy' : 'tych grup'
              } nie został przypisany żaden
              egzaminator.`}
            </>
          }
        />
      )}
    </>
  );
};
